import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ApiError,
  Cd4peApiError,
  GetUserDetailsResponseV1,
  handleQueryRequest,
  ListUsersResponseV1,
  MutationHook,
  QueryHook,
  QueryHookNoPayload,
  UserScopeV1,
  UserV1Service,
} from '@utils/api/cd4pe';
import {
  deleteUser,
  DeleteUserPayload,
  setSuperUser,
  SetSuperUserPayload,
  signUp,
  SignUpPayload,
  SignUpResponse,
  AjaxError,
} from '@services/cd4pe/ajax';
import queryClient from '@services/queryClient';

export const GET_USER_QUERY_TAG = 'getUserV1';
export const LIST_USERS_QUERY_TAG = 'listUsersByPrefix';
export const GET_USER_DETAILS_QUERY_TAG = 'getUserDetailsV1';

type UseGetUserDetailsV1 = QueryHook<
  typeof UserV1Service,
  'getUserDetailsV1',
  Cd4peApiError
>;

export const useGetUserDetailsV1: UseGetUserDetailsV1 = (payload) => {
  return useQuery<GetUserDetailsResponseV1, ApiError>(
    [GET_USER_DETAILS_QUERY_TAG, payload.userId, payload.workspaceId],
    () => handleQueryRequest(UserV1Service.getUserDetailsV1(payload)),
  );
};
/**
 * useForgotPasswordV1
 */
type UseForgotPasswordV1 = MutationHook<
  typeof UserV1Service,
  'forgotPasswordV1',
  Cd4peApiError
>;

export const useForgotPasswordV1: UseForgotPasswordV1 = () => {
  return useMutation((payload) =>
    handleQueryRequest(UserV1Service.forgotPasswordV1(payload)),
  );
};

/**
 * useResetPasswordV1
 */
type UseResetPasswordV1 = MutationHook<
  typeof UserV1Service,
  'resetPasswordV1',
  Cd4peApiError
>;

export const useResetPasswordV1: UseResetPasswordV1 = () => {
  return useMutation((payload) =>
    handleQueryRequest(UserV1Service.resetPasswordV1(payload)),
  );
};

/**
 * useGetUserV1
 */
type UseGetUserV1 = QueryHookNoPayload<
  typeof UserV1Service,
  'getUserV1',
  Cd4peApiError
>;

export const useGetUserV1: UseGetUserV1 = (options) =>
  useQuery(
    [GET_USER_QUERY_TAG],
    async () => handleQueryRequest(UserV1Service.getUserV1()),
    options,
  );

export const useSignUpV1 = () =>
  useMutation<SignUpResponse, AjaxError, SignUpPayload>(signUp);

/**
 * useUpdateUserV1
 */
type UseUpdateUserV1 = MutationHook<
  typeof UserV1Service,
  'updateUserV1',
  Cd4peApiError
>;

export const useUpdateUserV1: UseUpdateUserV1 = () => {
  return useMutation(
    (payload) => handleQueryRequest(UserV1Service.updateUserV1(payload)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_USER_QUERY_TAG]);
        queryClient.invalidateQueries([LIST_USERS_QUERY_TAG, '', 'GLOBAL']);
        queryClient.invalidateQueries([GET_USER_DETAILS_QUERY_TAG]);
      },
    },
  );
};

/**
 * useListUsersByPrefixV1
 */
type UseListUsersV1 = QueryHook<
  typeof UserV1Service,
  'listUsersV1',
  Cd4peApiError
>;

export const useListUsersByPrefix: UseListUsersV1 = (payload: {
  workspaceId: string;
  pageSize?: number;
  prefix?: string;
  scope?: UserScopeV1;
}) => {
  return useQuery<ListUsersResponseV1, ApiError>(
    [LIST_USERS_QUERY_TAG, payload.prefix, payload.scope],
    () => handleQueryRequest(UserV1Service.listUsersV1(payload)),
  );
};

export const useDeleteUser = (loggedInUsername: string) => {
  return useMutation<undefined, ApiError, DeleteUserPayload>(
    (payload) => handleQueryRequest(deleteUser(payload, loggedInUsername)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([LIST_USERS_QUERY_TAG]);
      },
    },
  );
};

export const useSetSuperUser = (loggedInUsername: string) => {
  return useMutation<undefined, ApiError, SetSuperUserPayload>(
    (payload) => handleQueryRequest(setSuperUser(loggedInUsername, payload)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([LIST_USERS_QUERY_TAG]);
      },
    },
  );
};
